/**
 * Typed, read-only projection over a REAL Atlas assessment engagement artifact
 * (apps/assessment/engagements/<id>/assessment.json). This mirrors the assessment engine's own output
 * shape — the web CONSUMES it, it does not recreate the engine (directive §9). Deterministic, no I/O
 * beyond the caller handing in the parsed artifact.
 *
 * Provenance: these are genuine Atlas-produced acceptance runs (environmentLabel "Astrionix build host
 * (on-host acceptance)", assessment-engine/1.0.0) — NOT fixtures. See docs/COMMAND-CENTER-DATA-PROVENANCE.md.
 */

import { available, unavailable, type Availability } from "./datasource";

export interface AssessmentEvidence {
  readonly evidenceId: string;
  readonly collectorId: string;
  readonly source: string;
  readonly kind: string;
  readonly observationKey: string;
  readonly collectedAt: string;
  readonly verification: "unverified" | "verified" | "rejected" | "stale";
  readonly confidence: number;
  readonly hash?: string;
  readonly excerpt?: string;
}
export interface AssessmentAsset { readonly entityId: string; readonly kind: string; readonly name: string }
export interface AssessmentFinding {
  readonly id: string;
  readonly controlId: string;
  readonly title: string;
  readonly summary: string;
  readonly severity: "critical" | "high" | "medium" | "low" | "informational";
  readonly category: string;
  readonly assets: readonly AssessmentAsset[];
  readonly relationships: readonly { readonly from?: string; readonly to?: string; readonly type?: string }[];
  readonly evidence: readonly { readonly evidenceId: string }[];
  readonly verification: "unverified" | "verified" | "rejected" | "stale";
}
export interface AssessmentControl { readonly id: string; readonly area: string; readonly title: string; readonly description: string; readonly method: string; readonly status: string; readonly findingIds?: readonly string[] }
export interface AssessmentRisk { readonly findingId: string; readonly score: number; readonly severity: string }
export interface AssessmentRecommendation { readonly id: string; readonly findingId: string; readonly action: string; readonly rationale: string; readonly priority: string; readonly evidenceIds: readonly string[] }
export interface AssessmentScope { readonly target: string; readonly roots: readonly string[]; readonly collectorsRun: readonly { readonly id: string; readonly status: string; readonly observationCount: number }[] }
export interface AssessmentExecutiveSummary { readonly headline?: string; readonly posture?: string; readonly counts?: Record<string, number>; readonly topRisks?: readonly unknown[]; readonly narrative?: string; readonly coverageNote?: string }
export interface AssessmentCertificate { readonly assessmentId: string; readonly algorithmVersion: string; readonly generatedAt: string; readonly snapshotHash: string; readonly scopeHash: string; readonly controlsHash: string; readonly findingsHash: string; readonly recommendationsHash: string; readonly selfHash: string }
export interface AssessmentMetadata { readonly id: string; readonly schemaVersion: string; readonly algorithmVersion: string; readonly environmentLabel: string; readonly generatedAt: string }

export interface Assessment {
  readonly metadata: AssessmentMetadata;
  readonly scope: AssessmentScope;
  readonly controls: readonly AssessmentControl[];
  readonly findings: readonly AssessmentFinding[];
  readonly evidence: readonly AssessmentEvidence[];
  readonly risk: readonly AssessmentRisk[];
  readonly recommendations: readonly AssessmentRecommendation[];
  readonly executiveSummary?: AssessmentExecutiveSummary;
  readonly certificate?: AssessmentCertificate;
  readonly history: readonly unknown[];
  readonly diff?: unknown;
}

export type Severity = AssessmentFinding["severity"];
const SEV_ORDER: readonly Severity[] = ["critical", "high", "medium", "low", "informational"];

/** TODAY metrics — every value defensible from the artifact. `changesSincePrevious` is Availability:
 *  an empty history means there is NO prior observation to compare, which is NOT the same as "0 changes". */
export interface AssessmentSummary {
  readonly environmentLabel: string;
  readonly generatedAt: string;
  readonly assetsObserved: Availability<number>;
  readonly evidenceRecords: number;
  readonly activeFindings: number;
  readonly verifiedFindings: number;
  readonly findingsBySeverity: Readonly<Record<Severity, number>>;
  readonly controlsAssessed: number;
  readonly changesSincePrevious: Availability<number>;
  readonly certified: boolean;
}

export function summarizeAssessment(a: Assessment): AssessmentSummary {
  const bySeverity = Object.fromEntries(SEV_ORDER.map((s) => [s, 0])) as Record<Severity, number>;
  for (const f of a.findings) bySeverity[f.severity] = (bySeverity[f.severity] ?? 0) + 1;
  const countsAssets = a.executiveSummary?.counts?.assets;
  return {
    environmentLabel: a.metadata.environmentLabel,
    generatedAt: a.metadata.generatedAt,
    // Prefer the engine's own authoritative asset count; else honest unavailable (don't guess from findings).
    assetsObserved: typeof countsAssets === "number" ? available(countsAssets) : unavailable("asset count not present in this artifact"),
    evidenceRecords: a.evidence.length,
    activeFindings: a.findings.length,
    verifiedFindings: a.findings.filter((f) => f.verification === "verified").length,
    findingsBySeverity: bySeverity,
    controlsAssessed: a.controls.length,
    // Empty history ⇒ no prior observation to diff (unknown), not "0 changes".
    changesSincePrevious: a.history.length > 0 ? available(a.history.length) : unavailable("no prior observation to compare"),
    certified: Boolean(a.certificate?.selfHash),
  };
}

/** Active findings ranked by severity for the FINDINGS cards. Pure ordering over real findings. */
export function rankAssessmentFindings(a: Assessment): readonly AssessmentFinding[] {
  return [...a.findings].sort((x, y) => SEV_ORDER.indexOf(x.severity) - SEV_ORDER.indexOf(y.severity));
}

/** Full evidence records supporting a finding — the evidence drawer chain. */
export function evidenceForFinding(a: Assessment, findingId: string): readonly AssessmentEvidence[] {
  const finding = a.findings.find((f) => f.id === findingId);
  if (!finding) return [];
  const ids = new Set(finding.evidence.map((e) => e.evidenceId));
  return a.evidence.filter((e) => ids.has(e.evidenceId));
}

export function recommendationForFinding(a: Assessment, findingId: string): AssessmentRecommendation | undefined {
  return a.recommendations.find((r) => r.findingId === findingId);
}
